import { type FC, lazy, Suspense, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

import { GlobalLoader, PageHeader, useErrorContext } from '@wf/sw-ui-kit';
import { GroupButton, Mode } from '@wf/components';

import OperationConfirmationModal from '../../components/OperationConfirmationModal';
import { useLicenseContext } from '../../LicenseModeContext';
import PaylicanPayment from '../PaylicanPayment';
import GenerateButton from '../GenerateButton';
import DocumentInfo from '../DocumentInfo';

import useOperationName from '../../../../hooks/useOperationName';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import useUpdateAccess from '../../../../hooks/useUpdateAccess';
import { supportInfoMessage } from '../../types/fieldEnums';
import { type OperationType } from '../../types/enums';

import { lpcoSubmittingSelector, operationsSelector, requestNumberSelector } from '../../store/selectors';
import { getIsKeyOperation } from '../../helpers/getIsKeyOperation';
import { lpcoOperation } from '../../store/actions';
import { resetErrors } from '../../store/slice';
import { COUNTRY } from '../../../../config';

const SonaliPayment = lazy(
  () =>
    import(
      process.env.REACT_APP_COUNTRY === 'bangladesh' ? '../SonaliPayment' : '../../../../components/DummyComponent'
    )
);

interface ILicenseHeaderProps {
  id: string;
}

const pageTitleByMode = {
  [Mode.create]: 'Fill New Application',
  [Mode.update]: 'Edit Application',
  [Mode.view]: 'View Application',
};

const LicenseHeader: FC<ILicenseHeaderProps> = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { setSubmittedValues } = useErrorContext();
  const { mode, viewMode } = useLicenseContext();
  const hasUpdateAccess = useUpdateAccess();

  const [submittedOperation, setSubmittedOperation] = useState<Nullable<OperationType>>(null);
  const isSubmitting = useSelector(lpcoSubmittingSelector);

  const operations = useSelector(operationsSelector);
  const requestNumber = useSelector(requestNumberSelector);
  const getOperationName = useOperationName();

  const { setValue, handleSubmit } = useFormContext();
  const { triggerBackendErrors } = useErrorContext();

  useUpdateEffect(() => {
    if (!operations.length && mode === Mode.update) {
      navigate(`../../view/${id}${search}`, { replace: true, relative: 'path' });
    }
  }, [operations, mode]);

  const onSubmit = handleSubmit(
    (data) => {
      dispatch(lpcoOperation(data));
      setSubmittedOperation(null);
      dispatch(resetErrors());
      setSubmittedValues(structuredClone(data));
    },
    (errors) => {
      !errors[supportInfoMessage] && setSubmittedOperation(null);
      triggerBackendErrors(); // in case if there are errors left from items adding or saving
      console.log(errors);
    }
  );

  const navigateBack = () => navigate(id ? '../..' : '..', { relative: 'path' });
  const navigateToEditMode = () => navigate(`../../edit/${id}`, { relative: 'path' });

  const sonaliPayment = (
    <Suspense fallback={null}>
      <SonaliPayment />
    </Suspense>
  );

  return (
    <>
      <PageHeader
        actions={
          <>
            <GroupButton>
              {!viewMode &&
                operations.map((operation) => (
                  <GroupButton.Button
                    onClick={() => {
                      setValue('selectedOperation', operation);
                      setSubmittedOperation(operation as OperationType);
                    }}
                    secondary={!getIsKeyOperation(operation)}
                    key={operation}
                    type="button"
                  >
                    {getOperationName(operation)}
                  </GroupButton.Button>
                ))}
              {COUNTRY === 'bangladesh' ? sonaliPayment : null}
              {COUNTRY === 'niger' ? <PaylicanPayment /> : null}
            </GroupButton>
            <GenerateButton />
          </>
        }
        onEdit={hasUpdateAccess && !!operations.length ? navigateToEditMode : undefined}
        title={t(pageTitleByMode[mode as keyof typeof pageTitleByMode])}
        onNavigateBack={navigateBack}
        tags={requestNumber}
        mode={mode}
      >
        <DocumentInfo id={id} />
      </PageHeader>
      {submittedOperation && (
        <OperationConfirmationModal
          onCancel={() => {
            setSubmittedOperation(null);
          }}
          operation={submittedOperation}
          onAccept={onSubmit}
        />
      )}
      {isSubmitting && <GlobalLoader />}
    </>
  );
};

export default LicenseHeader;
